import { Component, OnInit, inject } from '@angular/core';
import { IconService } from 'services/icon.service';
import { NavbarComponent } from './core/components/navbar/navbar.component';
import { RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LANGUAGE_STORAGE_KEY, UserSettingsService } from 'services/user-settings.service';
import { providerConfig } from '../providerconfig';
import { LoadingService } from 'services/loading.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';
import { ErrorPageComponent } from '@cyan/error-page';
import { Title } from '@angular/platform-browser';
import { AccountService } from 'services/account.service';
import { Observable, tap } from 'rxjs';
import { ColorScheme, ThemeService } from 'services/theme.service';
import { UpdateInfoDialogComponent } from './core/update-info-modal/update-info-dialog.component';
import { AppConfigService } from 'services/app-config.service';
import { MatDialog } from '@angular/material/dialog';
import { defaultDialogConfig } from 'shared/utils/dialog-config';
import { EulaDialogComponent } from './features/eula/eula-dialog/eula-dialog.component';

@Component({
  selector: 'core-ui-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    NavbarComponent,
    RouterOutlet,
    MatProgressSpinnerModule,
    AsyncPipe,
    ErrorPageComponent,
    TranslateModule
  ],
  providers: [IconService]
})
export class AppComponent implements OnInit {
  protected loading$: Observable<boolean>;
  protected companyName: string;
  protected colorScheme: ColorScheme = ColorScheme.LIGHT;
  private iconService = inject(IconService);
  private translate = inject(TranslateService);
  private loadingService = inject(LoadingService);
  private titleService = inject(Title);
  private accountService = inject(AccountService);
  private themeService = inject(ThemeService);
  private userSettingsService = inject(UserSettingsService);
  private dialog = inject(MatDialog);
  private appConfigService = inject(AppConfigService);

  constructor() {
    this.iconService.loadIcons();
    this.titleService.setTitle(providerConfig.productName);
    this.loading$ = this.loadingService.loading$;
  }

  public ngOnInit(): void {
    this.translate.setDefaultLang(this.getDefaultLanguage());
    this.themeService.updateColorScheme(this.userSettingsService.getUserSettings().colorScheme);
    this.themeService.colorScheme$.subscribe((colorScheme) => (this.colorScheme = colorScheme));

    this.accountService
      .getSelectedCompany$()
      .pipe(
        tap((company) => {
          this.companyName = company?.name;
        })
      )
      .subscribe();

    this.renderUpdateInfoDialog();
    this.renderEulaDialog();
  }

  private renderEulaDialog(): void {
    if (this.appConfigService.appConfig.eulaAcceptanceNeeded) {
      this.dialog.open(EulaDialogComponent, {
        ...defaultDialogConfig,
        disableClose: true,
        minWidth: '60vw'
      });
    }
  }

  private renderUpdateInfoDialog(): void {
    if (
      this.appConfigService.appConfig &&
      this.appConfigService.appConfig.updateInterval?.endDateTime > Math.floor(Date.now())
    ) {
      this.dialog.open(UpdateInfoDialogComponent, {
        ...defaultDialogConfig,
        minWidth: '60vw'
      });
    }
  }

  private getDefaultLanguage(): string {
    const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (storedLanguage && providerConfig.languages.includes(storedLanguage)) {
      return storedLanguage;
    }

    return providerConfig.defaultLanguage;
  }

  public get ColorScheme(): typeof ColorScheme {
    return ColorScheme;
  }
}
